import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FileExplorerDragStateService {
  private dragSource: { id?: unknown; type?: string } | null = null;

  /**
   * After drop on a tree row, the next click may be UA-synthesized (folder toggle, file select, etc.).
   * Cleared on consume or after a short timeout if no click reaches the handler.
   */
  private postDropSyntheticRowClickPending = false;
  private postDropSyntheticRowClickTimer: ReturnType<typeof setTimeout> | null =
    null;

  readonly hoverFolderId$ = new BehaviorSubject<unknown>(null);
  readonly rootDropHover$ = new BehaviorSubject(false);

  beginDrag(item: { id?: unknown; type?: string }): void {
    this.dragSource = item;
    this.hoverFolderId$.next(null);
    this.rootDropHover$.next(false);
    this.clearPostDropSyntheticRowClickArm();
  }

  endDrag(): void {
    this.dragSource = null;
    this.hoverFolderId$.next(null);
    this.rootDropHover$.next(false);
  }

  isDragging(): boolean {
    return this.dragSource != null;
  }

  getDragSource(): { id?: unknown; type?: string } | null {
    return this.dragSource;
  }

  setFolderHover(folderId: unknown | null): void {
    if (!this.dragSource) return;
    this.rootDropHover$.next(false);
    this.hoverFolderId$.next(folderId);
  }

  clearFolderHoverIfMatches(folderId: unknown): void {
    if (!this.dragSource) return;
    if (this.hoverFolderId$.value === folderId) {
      this.hoverFolderId$.next(null);
    }
  }

  /**
   * Root list area accepts folder reparent-to-root drops only; show highlight in that case.
   */
  setRootDropHover(active: boolean): void {
    if (!this.dragSource) return;
    if (this.dragSource.type !== 'folder') return;
    this.hoverFolderId$.next(null);
    this.rootDropHover$.next(active);
  }

  /** Call when a drop completed on any row (folder or file). */
  armPostDropSyntheticRowClick(): void {
    this.clearPostDropSyntheticRowClickArm();
    this.postDropSyntheticRowClickPending = true;
    this.postDropSyntheticRowClickTimer = setTimeout(() => {
      this.postDropSyntheticRowClickPending = false;
      this.postDropSyntheticRowClickTimer = null;
    }, 600);
  }

  private clearPostDropSyntheticRowClickArm(): void {
    if (this.postDropSyntheticRowClickTimer != null) {
      clearTimeout(this.postDropSyntheticRowClickTimer);
      this.postDropSyntheticRowClickTimer = null;
    }
    this.postDropSyntheticRowClickPending = false;
  }

  /**
   * Row `(click)` handlers should call first: if true, ignore action (synthetic post-drop click).
   */
  consumePostDropSyntheticRowClick(): boolean {
    if (!this.postDropSyntheticRowClickPending) {
      return false;
    }
    this.postDropSyntheticRowClickPending = false;
    if (this.postDropSyntheticRowClickTimer != null) {
      clearTimeout(this.postDropSyntheticRowClickTimer);
      this.postDropSyntheticRowClickTimer = null;
    }
    return true;
  }
}
